import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, UserPlus, Undo2, ArrowLeft } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';

export default function ClientEvaluatorGrants() {
  const { t, i18n } = useTranslation();
  const es = (i18n.language || '').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [evq, setEvq] = useState('');
  const [draft, setDraft] = useState({ evaluator_user_id: '', scope_type: 'employee', employee_id: '', department_id: '' });

  const reload = async () => {
    try {
      const [{ data: g }, { data: us }, { data: ds }] = await Promise.all([
        api.get('/client/evaluator-grants'),
        api.get('/client/users'),
        api.get('/client/departments'),
      ]);
      setItems(g.items || []);
      setPeople((us || []).filter((u) => u.status !== 'inactive'));
      setDepartments(ds || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); }, []);

  const evCandidates = useMemo(() => people.filter((p) => p.role === 'evaluated'
    && (!evq.trim() || `${p.name} ${p.position || ''}`.toLowerCase().includes(evq.trim().toLowerCase()))), [people, evq]);
  const active = items.filter((g) => g.status === 'active');
  const revoked = items.filter((g) => g.status !== 'active');

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/client/evaluator-grants', draft);
      toast.success(L('Evaluador designado autorizado', 'Designated evaluator authorized'));
      setOpen(false);
      setDraft({ evaluator_user_id: '', scope_type: 'employee', employee_id: '', department_id: '' });
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const revoke = async (gid) => {
    try {
      await api.post(`/client/evaluator-grants/${gid}/revoke`);
      toast.success(L('Autorización revocada', 'Authorization revoked'));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const scopeLabel = (g) => (g.scope_type === 'employee'
    ? `${L('Colaborador', 'Employee')}: ${g.employee?.name || g.employee_id}`
    : `${L('Departamento autorizado', 'Authorized department')}: ${(es && g.department?.name_es) || g.department?.name || g.department_id}`);

  const GrantRow = ({ g }) => (
    <li className="py-3 flex flex-wrap items-center gap-3" data-testid={`eg-grant-${g.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{g.evaluator?.name || g.evaluator_name}
          {g.evaluator?.position && <span className="text-xs text-muted-foreground"> · {g.evaluator.position}</span>}
        </p>
        <p className="text-xs text-muted-foreground">{scopeLabel(g)}</p>
        <p className="text-[11px] text-muted-foreground">
          {L('Autorizado por', 'Granted by')} {g.granted_by_name} · {new Date(g.granted_at).toLocaleDateString(es ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
          {g.revoked_at && ` — ${L('revocado', 'revoked')} ${new Date(g.revoked_at).toLocaleDateString(es ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`}
        </p>
      </div>
      <Badge variant="outline" className={g.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>
        {g.status === 'active' ? L('Activa', 'Active') : L('Revocada', 'Revoked')}
      </Badge>
      {g.status === 'active' && (
        <Button size="sm" variant="outline" className="gap-1 text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={() => revoke(g.id)} data-testid={`eg-revoke-${g.id}`}>
          <Undo2 className="h-3.5 w-3.5" /> {L('Revocar autorización', 'Revoke authorization')}
        </Button>
      )}
    </li>
  );

  return (
    <div className="space-y-6" data-testid="evaluator-grants-page">
      <Topbar title={L('Evaluadores Designados', 'Designated Evaluators')}
              subtitle={L('Autoriza a gerentes y supervisores a observar competencias sin otorgarles permisos administrativos.',
                'Authorize managers and supervisors to observe competencies without granting administrative permissions.')} />
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate('/client/talentcompetency/evo')} data-testid="eg-back">
          <ArrowLeft className="h-4 w-4" /> {L('Brechas de Competencias', 'Competency Gaps')}
        </Button>
        <Button className="gap-1.5 ml-auto" onClick={() => setOpen(true)} data-testid="eg-assign-btn">
          <UserPlus className="h-4 w-4" /> {L('Asignar evaluador', 'Assign evaluator')}
        </Button>
      </div>
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">{L('Autorizaciones activas', 'Active authorizations')}</h2>
          <Badge variant="outline">{active.length}</Badge>
        </div>
        {active.length === 0
          ? <p className="text-sm text-muted-foreground py-4" data-testid="eg-empty">{L('Aún no hay evaluadores designados. Usa "Asignar evaluador" para autorizar a un gerente o supervisor.', 'No designated evaluators yet. Use "Assign evaluator" to authorize a manager or supervisor.')}</p>
          : <ul className="divide-y" data-testid="eg-active-list">{active.map((g) => <GrantRow key={g.id} g={g} />)}</ul>}
      </Card>
      {revoked.length > 0 && (
        <Card className="p-5">
          <h2 className="font-semibold text-sm text-muted-foreground mb-2">{L('Historial de autorizaciones revocadas', 'Revoked authorization history')}</h2>
          <ul className="divide-y" data-testid="eg-revoked-list">{revoked.map((g) => <GrantRow key={g.id} g={g} />)}</ul>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="eg-assign-dialog">
          <DialogHeader>
            <DialogTitle>{L('Asignar evaluador designado', 'Assign designated evaluator')}</DialogTitle>
            <DialogDescription>
              {L('La persona conserva su rol de colaborador: solo obtiene acceso a las observaciones autorizadas.',
                'The person keeps their collaborator role: they only gain access to the authorized observations.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{L('Evaluador (colaborador de tu empresa)', 'Evaluator (company collaborator)')}</Label>
              <Input value={evq} onChange={(e) => setEvq(e.target.value)} placeholder={L('Buscar por nombre o puesto…', 'Search by name or position…')} data-testid="eg-ev-search" />
              <Select value={draft.evaluator_user_id} onValueChange={(v) => setDraft((d) => ({ ...d, evaluator_user_id: v }))}>
                <SelectTrigger data-testid="eg-ev-select"><SelectValue placeholder={L('Selecciona al evaluador…', 'Select the evaluator…')} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {evCandidates.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex flex-col text-left"><span>{p.name}</span>
                        {p.position && <span className="text-[11px] text-muted-foreground whitespace-normal">{p.position}</span>}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{L('Alcance de la autorización', 'Authorization scope')}</Label>
                <Select value={draft.scope_type} onValueChange={(v) => setDraft((d) => ({ ...d, scope_type: v, employee_id: '', department_id: '' }))}>
                  <SelectTrigger data-testid="eg-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">{L('Colaborador específico', 'Specific employee')}</SelectItem>
                    <SelectItem value="department">{L('Departamento completo', 'Entire department')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {draft.scope_type === 'employee' ? (
                <div className="space-y-1.5">
                  <Label>{L('Colaborador asignado', 'Assigned employee')}</Label>
                  <Select value={draft.employee_id} onValueChange={(v) => setDraft((d) => ({ ...d, employee_id: v }))}>
                    <SelectTrigger data-testid="eg-emp-select"><SelectValue placeholder={L('Selecciona…', 'Select…')} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {people.filter((p) => p.role === 'evaluated' && p.id !== draft.evaluator_user_id).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex flex-col text-left"><span>{p.name}</span>
                            {p.position && <span className="text-[11px] text-muted-foreground whitespace-normal">{p.position}</span>}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>{L('Departamento autorizado', 'Authorized department')}</Label>
                  <Select value={draft.department_id} onValueChange={(v) => setDraft((d) => ({ ...d, department_id: v }))}>
                    <SelectTrigger data-testid="eg-dept-select"><SelectValue placeholder={L('Selecciona…', 'Select…')} /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{(es && d.name_es) || d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {L('El alcance por departamento aplica al departamento ACTUAL de cada colaborador: las transferencias actualizan la autorización automáticamente.',
                'Department scope applies to each employee\u2019s CURRENT department: transfers update the authorization automatically.')}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
              <Button onClick={save} data-testid="eg-save"
                      disabled={saving || !draft.evaluator_user_id || (draft.scope_type === 'employee' ? !draft.employee_id : !draft.department_id)}>
                {saving ? '…' : L('Autorizar', 'Authorize')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
